// BuildBuildingMeshPlans.ts — PURE assembly of the Building's per-geometry-key MeshPlans from a
// `BuildingGeometryPlan` (Set2 §6). Set2 F3 restructures this from PER-ROOM to PER-FLOOR:
//
//   - `BuildFloorMeshPlan_Walls(floor)` builds the floor's WALLS from the merged `FloorWallPlan`
//     (§6.1) — one strip per merged segment, so a shared wall is emitted ONCE (Set1 built it once
//     per bordering room, producing the double-wall + z-fighting §0 motivation 1 describes).
//   - `BuildRoomMeshPlan_SlabAndCeiling(room, floor)` builds the per-room slab / floor finish /
//     ceiling (§6.2) over the room's INNER FACE.
//   - `BuildFloorMeshPlans(floor)` merges both into a `BuildingMeshPlanSet` {opaque, transparent}.
//   - `BuildBuildingMeshPlans(plan)` builds one set per unique FLOOR `geometryKey` (§6.4), so a
//     100-storey tower still shares one BufferGeometry across identical floors.
//
// Slot semantics ("wallExterior", "floorTop", …) are unchanged; the renderer maps slot → the
// owning room's recipe id per instance.
//
// Pure = no three / mobx-graphlink — the Node vitest lane imports this directly.
import type {BuildingGeometryPlan, BuildingFloorPlan, BuildingRoomInstance, WallSegment} from "../../Server/DB/mapObjects/Building/BuildingGeometry.js";
import {WallSolidBuilder_FloorSegments} from "./WallSolid.js";
import {BuildFloorSlab, BuildCeilingPlane} from "./FloorSlab.js";
import {BuildDoorModel, type OpeningBasis} from "./Door.js";
import {BuildWindowModel} from "./Window.js";
import {MeshBuilder, SplitPlanBySlots, type MeshPlan, type V3} from "./MeshPlan.js";

/** Semantic material slot names. The renderer maps each slot → the room's resolved recipe id. */
export const BUILDING_MATERIAL_SLOTS = {
	floorTop: "floorTop",
	floorUnderside: "floorUnderside",
	floorEdge: "floorEdge",
	ceiling: "ceiling",
	wallExterior: "wallExterior",
	wallInterior: "wallInterior",
	wallTop: "wallTop",
	wallReveal: "wallReveal",
	doorFrame: "doorFrame",
	doorSlab: "doorSlab",
	doorHardware: "doorHardware",
	windowFrame: "windowFrame",
	windowGlass: "windowGlass",
} as const;

/** Set2 §6.1: build the FLOOR's wall solid from the merged wall plan. One strip per merged segment
 *  (a shared wall is emitted once), each punched by the openings bound to THAT segment. z is
 *  relative to the floor's `floorTopZ`, so the walls span `[0, wallTopZ − floorTopZ]`. */
export function BuildFloorMeshPlan_Walls(floor: BuildingFloorPlan): MeshPlan {
	const b = new MeshBuilder();
	const walls = floor.walls;
	if (walls == null || walls.segments.length == 0) return b.build();

	const builder = new WallSolidBuilder_FloorSegments({
		segments: walls.segments,
		wallBaseZ: 0,
		wallTopZ: floor.wallTopZ - floor.floorTopZ,
		// The floor slab (or the ground) hides the wall bottom; a standalone closed solid would pass
		// `emitBottom: true` (the watertightness tests do).
		emitBottom: false,
	});
	for (const group of floor.openingsBySegment) {
		for (const op of group.openings) {
			builder.punch(group.segmentIndex, op.alongCenter, op.width, op.zBottom, op.zTop, BUILDING_MATERIAL_SLOTS.wallReveal);
		}
	}
	appendPlan(b, builder.build());
	return b.build();
}

/** Set2 §6.1/§6.2: the door/window MODEL meshes for a floor's bound openings. The segments come
 *  from the FLOOR wall plan's per-segment INTERIOR face (`innerStart`/`innerEnd`), i.e. the SAME
 *  geometry the walls were punched against and the openings were bound against — so
 *  `segmentIndex`/`alongCenter`/`width` mean one thing across punch, reveal, and model (the doc §5
 *  matched-pair requirement). `wallThickness` is the SEGMENT's resolved (max-wins) thickness, so a
 *  door on a shared wall fills the full reveal. */
export function BuildOpeningModelsForFloor(floor: BuildingFloorPlan): MeshPlan {
	const b = new MeshBuilder();
	const walls = floor.walls;
	if (walls == null) return b.build();
	// Interior-face segments, index-aligned with `walls.segments`.
	const faceSegments: WallSegment[] = walls.segments.map(s=>{
		const dx = s.innerEnd[0] - s.innerStart[0];
		const dy = s.innerEnd[1] - s.innerStart[1];
		const length = Math.hypot(dx, dy) || 1e-9;
		return {
			start: s.innerStart, end: s.innerEnd, length,
			// The face runs parallel to the centerline in the same direction, so the outward normal
			// (right of travel for the CCW ring) is the segment's own normal.
			normal: s.normal,
		};
	});
	for (const group of floor.openingsBySegment) {
		const seg = faceSegments[group.segmentIndex];
		const planSeg = walls.segments[group.segmentIndex];
		if (seg == null || planSeg == null) continue;
		for (const op of group.openings) {
			const basis = ComputeOpeningBasis(seg, op.alongCenter, op.zBottom);
			if (op.kind == "window") {
				b.append(BuildWindowModel({
					width: op.width,
					height: op.height,
					paneDivisionsX: op.model.window.paneDivisionsX,
					paneDivisionsY: op.model.window.paneDivisionsY,
					wallThickness: planSeg.wallThickness,
				}, basis));
			} else if (op.kind == "door") {
				b.append(BuildDoorModel({
					width: op.width,
					height: op.height,
					swing: op.model.door.swing,
					openAngle: op.model.door.openAngle,
					wallThickness: planSeg.wallThickness,
				}, basis));
			}
			// kind == "opening": the wall is fully punched out, so there is NO model mesh.
		}
	}
	return b.build();
}

/** Set2 §6.2: one room's slab + floor finish + ceiling, in BUILDING-LOCAL coords with z relative to
 *  the floor's `floorTopZ`. This is the part of the Set1 per-room plan that is genuinely per-room. */
export function BuildRoomMeshPlan_SlabAndCeiling(room: BuildingRoomInstance): MeshPlan {
	const b = new MeshBuilder();

		// --- Floor slab: spans `[-floorThickness, 0]`. Set2 §6.2 fix: extruded over the room's
		// SLAB RING — flush with the centerline on shared edges (the two rooms' slabs meet there,
		// jointly supporting the shared wall) and expanded to the OUTER face on exterior edges (the
		// full exterior wall rests on the slab instead of half of it overhanging). The floorTop
		// FINISH plane below stays on the inner face so it doesn't poke into the wall.
		if (room.hasFloor) {
			const slab = BuildFloorSlab({
					outerRing: room.slabRing,
			baseZ: -room.floorThickness,
			topZ: 0, // top face at the floor plane
			topSlot: BUILDING_MATERIAL_SLOTS.floorTop,
			bottomSlot: BUILDING_MATERIAL_SLOTS.floorUnderside,
			edgeSlot: BUILDING_MATERIAL_SLOTS.floorEdge,
		});
		appendPlan(b, slab);
	}

	// --- Ceiling plane: a ZERO-THICKNESS downward face at the wall top, emitted ONLY for the top
	// occupied floor (interior floors get their ceiling from the slab above's `floorUnderside`; a
	// second coplanar ceiling would z-fight). Over the INNER FACE so it stops at the room's real
	// face instead of poking into the walls.
	if (room.hasCeiling && room.ceilingZ != null && !room.hasFloorAbove) {
		appendPlan(b, BuildCeilingPlane({
			footprint: room.innerFace,
			z: room.ceilingZ,
			slot: BUILDING_MATERIAL_SLOTS.ceiling,
		}));
	}

	return b.build();
}

function appendPlan(b: MeshBuilder, plan: MeshPlan): void {
	const offset = b.positions.length / 3;
	b.positions.push(...plan.positions);
	b.normals.push(...plan.normals);
	b.uvs.push(...plan.uvs);
	for (const g of plan.groups) {
		b.groups.push({materialSlot: g.materialSlot, start: g.start + offset, count: g.count});
	}
}

/** Build the wall segments (in building-local XY) from a closed CCW ring, matching the
 *  `WallSolid` convention: segment `i` spans ring[i]→ring[i+1] with the OUTWARD normal. Used to
 *  compute each opening's segment frame (tangent/normal/up) where a wheel of ring-shaped segments
 *  is still the natural input (tests, and the F1 ring-based builder). Set2 F3's FLOOR path uses
 *  `BuildOpeningModelsForFloor`, which reads the merged segments' interior faces instead. */
export function ComputeRoomSegments(closedFootprint: [number, number][]): WallSegment[] {
	const segments: WallSegment[] = [];
	for (let i = 0; i < closedFootprint.length - 1; i++) {
		const start = closedFootprint[i];
		const end = closedFootprint[i + 1];
		const dx = end[0] - start[0];
		const dy = end[1] - start[1];
		const length = Math.hypot(dx, dy);
		if (length < 1e-9) continue;
		// Outward normal (right of travel for a CCW interior-on-left ring).
		const n: [number, number] = [dy / length, -dx / length];
		segments.push({start, end, length, normal: n});
	}
	return segments;
}

/** The building-local basis for an opening's model frame. `tangent` = along-wall (segment start →
 *  end), `normal` = the segment's OUTWARD horizontal unit normal, `up` = +Z, `pos` = the frame
 *  origin: the interior wall face at the opening's `alongCenter` (x) and `zBottom` (z). Model coords
 *  map x → along, y → up, z → outward (`z ∈ [0, wallThickness]`), as `Door.ts`/`Window.ts` document.
 *  `seg` MUST be the INTERIOR face segment the opening was bound against — see
 *  `BuildOpeningModelsForFloor`. */
export function ComputeOpeningBasis(seg: WallSegment, alongCenter: number, zBottom: number): OpeningBasis {
	const len = seg.length || 1;
	const tangent: V3 = [(seg.end[0] - seg.start[0]) / len, (seg.end[1] - seg.start[1]) / len, 0];
	const normal: V3 = [seg.normal[0], seg.normal[1], 0];
	const up: V3 = [0, 0, 1];
	// The model x = 0 is the opening CENTER; the frame origin is the interior face at `alongCenter`.
	const pos: V3 = [
		seg.start[0] + tangent[0] * alongCenter,
		seg.start[1] + tangent[1] * alongCenter,
		zBottom,
	];
	return {t: tangent, n: normal, up, pos};
}

/** A per-geometry-key pair of MeshPlans: `opaque` holds all non-glass geometry (walls, floors,
 *  frame, sash, slab — rendered with `castShadow`/`receiveShadow`), and `transparent` holds the
 *  windowGlass panes (rendered as a distinct transparent pass with `transparent: true,
 *  depthWrite: false`, after the opaque geometry — §4.4). Repeated floors share one geometry key,
 *  so the pair is built once per key regardless of how many floors claim it. */
export type BuildingMeshPlanSet = {opaque: MeshPlan, transparent: MeshPlan};

const TRANSPARENT_SLOTS: ReadonlySet<string> = new Set([BUILDING_MATERIAL_SLOTS.windowGlass]);

/** Set2 §6.3: build ONE FLOOR's full plan (merged walls + opening models + every room's
 *  slab/finish/ceiling), then split out the transparent (glass) pass. */
export function BuildFloorMeshPlans(floor: BuildingFloorPlan): BuildingMeshPlanSet {
	const b = new MeshBuilder();
	appendPlan(b, BuildFloorMeshPlan_Walls(floor));
	appendPlan(b, BuildOpeningModelsForFloor(floor));
	for (const room of floor.rooms) appendPlan(b, BuildRoomMeshPlan_SlabAndCeiling(room));
	const full = b.build();
	const {included, excluded} = SplitPlanBySlots(full, new Set<string>(TRANSPARENT_SLOTS));
	return {opaque: excluded, transparent: included};
}

/** Build the per-FLOOR-geometry-key MeshPlan sets for a `BuildingGeometryPlan`. Repeated floors
 *  sharing a `geometryKey` are built once (§6.4). Returns `null` when there are no floors. */
export function BuildBuildingMeshPlans(plan: BuildingGeometryPlan|n): Map<string, BuildingMeshPlanSet>|null {
	if (plan == null) return null;
	const out = new Map<string, BuildingMeshPlanSet>();
	for (const floor of plan.floors) {
		if (floor.walls == null) continue; // gap floor
		if (!out.has(floor.geometryKey)) {
			out.set(floor.geometryKey, BuildFloorMeshPlans(floor));
		}
	}
	return out.size ? out : null;
}
